import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Download, Lock, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { api } from "../../lib/api";

export const WIZARD_STEPS = [
  { n: 1, label: "Get my report" },
  { n: 2, label: "Tell us about your business" },
  { n: 3, label: "Upload your documents" },
  { n: 4, label: "We score your business" },
  { n: 5, label: "Your Score & Report" },
];

export const WizardStepper = ({ current }) => (
  <div className="rounded-2xl border border-neutral-200 bg-white px-4 py-4 sm:px-6" data-testid="onboarding-wizard-stepper">
    <div className="flex items-center justify-between gap-1 sm:gap-2">
      {WIZARD_STEPS.map((s, idx) => {
        const done = current > s.n;
        const active = current === s.n;
        return (
          <div key={s.n} className="flex min-w-0 flex-1 items-center gap-1 sm:gap-2">
            <div className="flex min-w-0 flex-col items-center gap-1 text-center sm:flex-1">
              <span data-testid={`wizard-step-dot-${s.n}`}
                className={"flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold " +
                  (done ? "bg-[#0A8B85] text-white"
                    : active ? "bg-[#0A8B85]/15 text-[#0A8B85] ring-2 ring-[#0A8B85]"
                    : "bg-neutral-100 text-neutral-400")}>
                {done ? <Check className="h-3.5 w-3.5" /> : s.n}
              </span>
              <span className={"hidden truncate text-[11px] font-semibold sm:block " +
                (active ? "text-[#0A8B85]" : done ? "text-neutral-600" : "text-neutral-400")}>
                {s.label}
              </span>
            </div>
            {idx < WIZARD_STEPS.length - 1 && (
              <div className={"mb-4 hidden h-0.5 flex-1 rounded sm:block " + (done ? "bg-[#0A8B85]" : "bg-neutral-200")} />
            )}
          </div>
        );
      })}
    </div>
    <p className="mt-2 text-center text-xs font-semibold text-neutral-500 sm:hidden">
      Step {current} of 5 · {WIZARD_STEPS[current - 1].label}
    </p>
  </div>
);

export const WizardNav = ({ onBack, onNext, nextLabel = "Next", nextDisabled, hint, busy }) => (
  <div className="flex flex-wrap items-center justify-between gap-3">
    <div>
      {onBack && (
        <button type="button" onClick={onBack} data-testid="wizard-back"
          className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-4 py-2.5 text-sm font-semibold text-neutral-600 hover:bg-neutral-50">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
      )}
    </div>
    <div className="flex items-center gap-3">
      {nextDisabled && hint && <p className="max-w-xs text-right text-xs text-neutral-500" data-testid="wizard-hint">{hint}</p>}
      {onNext && (
        <button type="button" onClick={onNext} disabled={nextDisabled || busy} data-testid="wizard-next"
          className="inline-flex items-center gap-2 rounded-lg bg-[#0A8B85] px-6 py-2.5 text-sm font-bold text-white hover:bg-[#066667] disabled:cursor-not-allowed disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {nextLabel} <ArrowRight className="h-4 w-4" />
        </button>
      )}
    </div>
  </div>
);

// Step 5 — download the Capital Readiness Report. Founders must unlock
// (payment/credits) and download before the dashboard opens up.
export const ReportDownloadStep = () => {
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  useEffect(() => {
    api.get("/founder/credits/balance")
      .then((r) => setInfo(r.data || {}))
      .catch(() => setInfo({}));
  }, []);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/founder/readiness/report.pdf",
        { responseType: "blob", timeout: 180000 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "clinq-capital-readiness-report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDownloaded(true);
      toast.success("Report downloaded — your dashboard is now open!");
    } catch (e) {
      toast.error(e?.response?.status === 403
        ? "Get your report first, then come back to download it."
        : "Couldn't download the report — please try again.");
    } finally { setBusy(false); }
  };

  if (info === null) {
    return <div className="rounded-2xl border border-neutral-200 bg-white p-8 text-center text-sm text-neutral-500"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;
  }
  const unlocked = !!info.report_unlocked;
  return (
    <section className="rounded-2xl border border-neutral-200 bg-white p-6 sm:p-8" data-testid="wizard-report-step">
      <h2 className="text-lg font-bold uppercase tracking-wider text-[#0A8B85]">Download your Capital Readiness Report</h2>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-neutral-600">
        Your Capital Readiness Report breaks down exactly how your Capital Readiness Score was calculated, your strengths,
        the gaps, and what to fix to improve your position with funders — your personal
        capital raising roadmap.
      </p>
      <div className="mt-6">
        {unlocked ? (
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={download} disabled={busy} data-testid="wizard-download-report"
              className="inline-flex items-center gap-2 rounded-lg bg-[#0A8B85] px-6 py-3 text-sm font-bold text-white hover:bg-[#066667] disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Download Your Capital Readiness Report
            </button>
            {downloaded && <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600"><Check className="h-4 w-4" /> Downloaded</span>}
          </div>
        ) : (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5" data-testid="wizard-report-locked">
            <p className="flex items-center gap-2 text-sm font-bold text-amber-800">
              <Lock className="h-4 w-4" /> Your full report is ready — unlock it to download
            </p>
            <p className="mt-1 text-sm text-amber-700">
              Unlocking gives you the complete PDF report plus your improvement roadmap.
              This takes about 2 minutes.
            </p>
            <button type="button" data-testid="wizard-unlock-report"
              onClick={() => nav("/founder/readiness-report")}
              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#0A8B85] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#066667]">
              Unlock &amp; download my report <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
      <div className="mt-8 border-t border-neutral-100 pt-5">
        <button type="button" data-testid="wizard-go-dashboard" disabled={!downloaded}
          onClick={() => nav("/founder/dashboard")}
          className="inline-flex items-center gap-2 rounded-lg bg-neutral-900 px-6 py-3 text-sm font-bold text-white hover:bg-neutral-700 disabled:cursor-not-allowed disabled:opacity-30">
          Go to my dashboard <ArrowRight className="h-4 w-4" />
        </button>
        {!downloaded && (
          <p className="mt-2 text-xs text-neutral-500">
            Download your report above to open your dashboard.
          </p>
        )}
      </div>
    </section>
  );
};
